import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Sprout, Layers, Coins, RefreshCcw, Droplets, SprayCan, Wheat, Award, FileCheck, Stamp, Globe2, FileDown } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export const SectionTitle = ({ title, subtitle }) => (
  <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="mb-14">
    <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-brand-dark uppercase">{title}</h2>
    {subtitle && <p className="mt-3 text-brand-brown text-base md:text-lg max-w-2xl font-semibold">{subtitle}</p>}
  </motion.div>
);

const VALUE_ICONS = [Sprout, Layers, Coins, RefreshCcw];

export const ValueProps = () => {
  const { t } = useTranslation();
  const items = t("value.items", { returnObjects: true });
  return (
    <section className="bg-brand-cream py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionTitle title={t("value.title")} subtitle={t("value.subtitle")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-3xl p-8 border border-brand-green/15 shadow-[0_8px_30px_rgba(109,76,65,0.08)] hover:-translate-y-1 transition-transform">
                <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center text-brand-dark">
                  <Icon size={26} />
                </div>
                <h3 className="font-heading text-2xl text-brand-dark mt-5 uppercase">{item.title}</h3>
                <p className="text-sm text-brand-brown mt-2 leading-relaxed">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const HOW_ICONS = [Droplets, SprayCan, Wheat];

export const HowItWorks = () => {
  const { t } = useTranslation();
  const steps = t("how.steps", { returnObjects: true });
  return (
    <section className="bg-brand-dark py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.h2 {...fadeUp} transition={{ duration: 0.6 }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl text-brand-yellow uppercase mb-14">
          {t("how.title")}
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s, i) => {
            const Icon = HOW_ICONS[i];
            return (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.12 }} className="relative">
                <div className="font-heading text-8xl text-white/10 absolute -top-6 -start-2">{i + 1}</div>
                <div className="w-14 h-14 rounded-2xl bg-brand-orange flex items-center justify-center text-white mb-5">
                  <Icon size={26} />
                </div>
                <h3 className="font-heading text-3xl text-white uppercase">{s.title}</h3>
                <p className="text-sm text-white/70 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
        <motion.a {...fadeUp} transition={{ duration: 0.5, delay: 0.2 }}
          href="/guides/agrodyke-user-guide.pdf" target="_blank" rel="noopener noreferrer"
          data-testid="user-guide-pdf-link"
          className="mt-12 inline-flex items-center gap-3 px-6 py-4 rounded-full bg-brand-yellow text-brand-dark font-bold text-sm hover:bg-white transition-colors">
          <FileDown size={20} className="shrink-0" />
          <span>User Guide — Agrodyke Dosage for Regenerative Agriculture (PDF)</span>
        </motion.a>
      </div>
    </section>
  );
};

const CERT_ICONS = [Award, FileCheck, Stamp];

const CERT_DOCS = [
  "https://customer-assets-4nw71qhi.emergentagent.net/job_organic-powder-hub-1/artifacts/2ik1pmat_Hasil%20Uji%20Lab%20Malaysia.jpg",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_organic-powder-hub-1/artifacts/ywp6h02k_Hasil%20Uji%20Lab%20India.png",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_organic-powder-hub-1/artifacts/dkbbms0o_Piagam%20Tanda%20Kehormatan%20Presiden%20RI.jpg",
  "/images/iso-9001.jpg",
];

export const Certifications = () => {
  const { t } = useTranslation();
  const items = t("cert.items", { returnObjects: true });
  const docs = t("cert.docs", { returnObjects: true });
  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionTitle title={t("cert.title")} subtitle={t("cert.subtitle")} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, i) => {
            const Icon = CERT_ICONS[i];
            return (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex items-start gap-5 bg-brand-cream rounded-3xl p-8 border border-brand-green/15">
                <div className="w-14 h-14 shrink-0 rounded-full bg-brand-green flex items-center justify-center text-white">
                  <Icon size={24} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl text-brand-dark uppercase">{item.title}</h3>
                  <p className="text-sm text-brand-brown mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {CERT_DOCS.map((src, i) => (
            <motion.a key={i} href={src} target="_blank" rel="noopener noreferrer"
              data-testid={`cert-doc-${i}`}
              {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="block bg-brand-cream rounded-3xl p-4 border border-brand-green/15 hover:-translate-y-1 transition-transform">
              <div className="h-72 rounded-2xl overflow-hidden bg-white">
                <img src={src} alt={docs[i]} loading="lazy" className="w-full h-full object-contain" />
              </div>
              <p className="text-xs font-bold text-brand-brown mt-3 text-center">{docs[i]}</p>
            </motion.a>
          ))}
        </div>
        <motion.div {...fadeUp} transition={{ duration: 0.5 }}
          data-testid="global-reach-note"
          className="mt-10 bg-brand-dark rounded-3xl p-8 sm:p-10 relative overflow-hidden">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 shrink-0 rounded-2xl bg-brand-yellow flex items-center justify-center text-brand-dark">
              <Globe2 size={26} />
            </div>
            <h3 className="font-heading text-3xl sm:text-4xl text-brand-yellow uppercase">{t("cert.globalTitle")}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">{t("cert.africa")}</div>
              <div className="flex flex-wrap gap-2">
                {t("cert.countriesAfrica", { returnObjects: true }).map((c, i) => (
                  <span key={i} data-testid={`country-africa-${i}`}
                    className="px-4 py-2 rounded-full bg-white/10 border border-white/15 text-sm font-semibold text-white hover:bg-brand-orange transition-colors">
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">{t("cert.asia")}</div>
              <div className="flex flex-wrap gap-2">
                {t("cert.countriesAsia", { returnObjects: true }).map((c, i) => (
                  <span key={i} data-testid={`country-asia-${i}`}
                    className="px-4 py-2 rounded-full bg-white/10 border border-white/15 text-sm font-semibold text-white hover:bg-brand-orange transition-colors">
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-sm text-white/60 leading-relaxed">{t("cert.global")}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
